/**
 * Asset category content for public information pages.
 * Each entry powers a dedicated /assets/:slug page.
 */

export const ASSET_HUB_PATH = '/assets';

export const assetCategories = [
  {
    slug: 'bank-fd',
    path: '/assets/bank-fd',
    label: 'Bank & FD',
    gridLabel: 'Bank & FD',
    pillLabel: 'Bank & FD',
    variant: 'mint',
    seo: {
      title: 'Bank Accounts & Fixed Deposits',
      description:
        'Learn why bank accounts and fixed deposits must be recorded for your family. Store account details, FD receipts, and nominee information securely with KeepMyAsset.',
      keywords:
        'bank account nominee, fixed deposit family, unclaimed bank money India, FD nominee alert, savings account records',
    },
    hero: {
      eyebrow: 'BANK & FIXED DEPOSITS',
      title: 'Your bank accounts should never become a family mystery',
      lead: 'Savings accounts, salary accounts, and fixed deposits are often the first assets families search for—but only if they know they exist.',
    },
    intro: [
      'Most Indian families hold money across multiple banks: a salary account, a savings account opened years ago, fixed deposits for tax saving, and sometimes accounts that were opened and forgotten. When the account holder passes away, families often spend months—or years—trying to find where the money was kept.',
      'Banks do not automatically tell your family about every account you hold. Nominee details help, but they are not always updated, and many families simply do not know which bank to contact first.',
      'KeepMyAsset gives you one secure place to record every bank account and fixed deposit, attach key documents, and tell your nominees exactly where to begin.',
    ],
    whyMatters: {
      title: 'Why bank and FD records matter so much',
      points: [
        'Billions of rupees sit unclaimed in Indian banks because families never knew the accounts existed.',
        'Fixed deposits often mature quietly while families assume the money was already moved.',
        'Joint accounts, dormant accounts, and locker-linked accounts are easy to overlook during a stressful time.',
        'Nominee details at the bank help—but only if the family knows which bank and branch to visit.',
      ],
    },
    whatToStore: {
      title: 'What to record for each bank account or FD',
      items: [
        'Bank name and branch (city and IFSC code if you know it)',
        'Account type—savings, current, salary, or fixed deposit',
        'Last four digits of the account number (never store full numbers in unsecured places)',
        'Fixed deposit receipt or maturity date',
        'Nominee name registered at the bank',
        'Bank locker details, if you have one',
        'Any auto-debit or linked investment accounts',
        'Contact person at the branch, if you have a relationship manager',
      ],
    },
    commonProblems: {
      title: 'Common problems families face',
      items: [
        'Father had three bank accounts but the family only knew about one.',
        'An FD matured years ago but no one received the credit because the registered mobile number was old.',
        'Nominee was never updated after marriage or the birth of a child.',
        'Bank locker keys were found, but no one knew which branch the locker belonged to.',
        'Passbook and cheque books were stored in different places with no central list.',
      ],
    },
    tips: {
      title: 'Simple tips that make a big difference',
      items: [
        'Review nominee details at every bank at least once a year.',
        'Keep a simple list of all accounts—even small ones with low balances.',
        'Note FD maturity dates so your family can follow up on time.',
        'Tell at least one trusted person that you maintain a secure record with KeepMyAsset.',
        'Upload a photo of passbooks or FD receipts—not to replace the bank, but to guide your family.',
      ],
    },
    faqs: [
      {
        q: 'Should I store my full bank account number?',
        a: 'We recommend recording the bank name, branch, account type, and last four digits. That is usually enough for your family to start a conversation with the bank without exposing full account numbers unnecessarily.',
      },
      {
        q: 'Does KeepMyAsset transfer money from my bank?',
        a: 'No. We help your family find and understand what exists. All claims and transfers happen directly with the bank, using their official process.',
      },
      {
        q: 'What if my nominee at the bank is different from my KeepMyAsset nominee?',
        a: 'Bank nominees are legally registered with the bank. KeepMyAsset nominees receive guidance when a verified family request is approved. It is best to keep both updated and aligned.',
      },
    ],
    related: ['ppf', 'insurance', 'loans'],
  },
  {
    slug: 'ppf',
    path: '/assets/ppf',
    label: 'PPF',
    gridLabel: 'PPF',
    variant: 'mint',
    seo: {
      title: 'Public Provident Fund (PPF)',
      description:
        'Guide your family to your PPF account. Learn what to record, why PPF goes unclaimed, and how KeepMyAsset helps nominees find your provident fund savings.',
      keywords: 'PPF nominee, public provident fund family, PPF unclaimed money, PPF account records India',
    },
    hero: {
      eyebrow: 'PUBLIC PROVIDENT FUND (PPF)',
      title: 'Your PPF savings deserve to reach the people you choose',
      lead: 'PPF is one of India\'s most trusted long-term savings tools—but families often discover it only after years of contributions have grown quietly.',
    },
    intro: [
      'The Public Provident Fund (PPF) is a 15-year government-backed savings scheme offered through banks and post offices. Many salaried and self-employed Indians contribute every year for tax benefits and safe, steady growth.',
      'Because PPF runs for such a long time, it is easy for family members to forget it exists—especially if statements arrive by email, the passbook sits in a drawer, or the account was opened at a branch someone no longer visits.',
      'Recording your PPF details today means your family will not have to search through old papers or guess which post office or bank holds your account.',
    ],
    whyMatters: {
      title: 'Why PPF records are often missed',
      points: [
        'PPF accounts can run for decades with automatic yearly contributions.',
        'Families may know about EPF from employers but forget separate PPF accounts.',
        'Nominee details at the post office or bank are not always filled in.',
        'Matured PPF balances can sit unwithdrawn if no one knows the account exists.',
      ],
    },
    whatToStore: {
      title: 'What to record for your PPF account',
      items: [
        'Bank or post office where the PPF account is held',
        'PPF account number (or last few digits if you prefer)',
        'Date the account was opened and approximate maturity date',
        'Nominee registered with the bank or post office',
        'Annual contribution amount (optional, for context)',
        'Passbook photo or latest account statement',
        'Linked savings account used for contributions',
      ],
    },
    commonProblems: {
      title: 'What goes wrong without a clear record',
      items: [
        'Family knew about salary savings but not a separate PPF opened years earlier.',
        'Account was at a post office in a hometown the younger generation rarely visits.',
        'Nominee field was left blank when the account was opened.',
        'Maturity happened but no one received a reminder because contact details were outdated.',
      ],
    },
    tips: {
      title: 'Helpful habits for PPF holders',
      items: [
        'Check nominee details when you make your annual PPF deposit.',
        'Save one statement or passbook photo each year in KeepMyAsset.',
        'Note whether your PPF is at a bank or post office—families often search the wrong place first.',
        'If you have both PPF and EPF, record them separately so nothing is grouped incorrectly.',
      ],
    },
    faqs: [
      {
        q: 'Is PPF the same as EPF?',
        a: 'No. PPF is a voluntary personal savings scheme you open yourself. EPF is usually linked to employment and managed through your employer. Many people have both—and families should know about each one.',
      },
      {
        q: 'Can my family withdraw PPF through KeepMyAsset?',
        a: 'No. KeepMyAsset helps your family locate and understand your PPF account. Withdrawal or transfer follows the official rules of the bank or post office where your PPF is held.',
      },
    ],
    related: ['epf', 'bank-fd', 'ppf-epf'],
  },
  {
    slug: 'epf',
    path: '/assets/epf',
    label: 'EPF',
    gridLabel: 'EPF',
    variant: 'mint',
    seo: {
      title: 'Employee Provident Fund (EPF)',
      description:
        'Help your family find your EPF and EPS balances. Learn what to store about your provident fund, UAN, and past employers with KeepMyAsset.',
      keywords: 'EPF nominee, UAN family, employee provident fund unclaimed, EPS pension records India',
    },
    hero: {
      eyebrow: 'EMPLOYEE PROVIDENT FUND (EPF)',
      title: 'Your years of work built EPF savings—make sure your family can find them',
      lead: 'EPF balances from current and past jobs add up over a lifetime. Without a clear record, even large amounts can go unclaimed.',
    },
    intro: [
      'If you have ever been employed in India, you likely have an Employee Provident Fund (EPF) account. A portion of your salary was deposited every month, often matched by your employer. Over years, this becomes a meaningful retirement corpus.',
      'Many people change jobs several times. Each employer may have contributed to the same Universal Account Number (UAN), but families rarely know the UAN, past employer names, or how to check the balance on the EPFO portal.',
      'A simple record of your EPF details saves your family weeks of confusion and helps them approach the EPFO with the right information from day one.',
    ],
    whyMatters: {
      title: 'Why EPF is one of the most commonly unclaimed assets',
      points: [
        'Job changes leave behind EPF balances families never knew existed.',
        'UAN numbers are often known only to the employee, not the spouse or children.',
        'EPS (pension) benefits are separate from the main EPF balance and easily overlooked.',
        'Families may assume "salary stopped, so nothing is left" when a large balance still exists.',
      ],
    },
    whatToStore: {
      title: 'What to record for EPF',
      items: [
        'Your UAN (Universal Account Number)',
        'Current and past employer names with approximate employment dates',
        'Latest EPF passbook download or balance screenshot',
        'Nominee details registered with EPFO, if any',
        'Linked Aadhaar and bank account for EPF withdrawal (for reference)',
        'EPS (pension) eligibility notes if you are unsure',
        'Any pending transfer requests between old and new employers',
      ],
    },
    commonProblems: {
      title: 'Real situations families struggle with',
      items: [
        'Parent worked at five companies over 30 years—family only knew the last one.',
        'UAN was never shared with the spouse.',
        'EPF balance was transferred but no one tracked the final consolidated amount.',
        'Nominee at EPFO was an old address or relative who is no longer reachable.',
      ],
    },
    tips: {
      title: 'Practical steps for EPF holders',
      items: [
        'Download your EPF passbook once a year and store it securely in KeepMyAsset.',
        'After every job change, note the old employer and confirm the transfer status.',
        'Share your UAN location (not the password) with your chosen nominee through KeepMyAsset.',
        'Check whether EPS pension eligibility applies—many families miss this entirely.',
      ],
    },
    faqs: [
      {
        q: 'Where do I find my UAN?',
        a: 'Your UAN is on your salary slip, EPFO portal account, or EPF passbook. If you are employed, your HR team can also confirm it.',
      },
      {
        q: 'Does KeepMyAsset access my EPFO account?',
        a: 'No. We store the information you choose to upload so your family knows where to look. All EPF claims are processed through EPFO directly.',
      },
    ],
    related: ['ppf', 'bank-fd', 'ppf-epf'],
  },
  {
    slug: 'ppf-epf',
    path: '/assets/ppf-epf',
    label: 'PPF & EPF',
    pillLabel: 'PPF & EPF',
    variant: 'mint',
    seo: {
      title: 'PPF & EPF — Government Savings for Your Family',
      description:
        'Understand the difference between PPF and EPF, why both matter, and how to record provident fund details so your family never misses your hard-earned savings.',
      keywords: 'PPF and EPF guide, provident fund family India, PPF vs EPF nominee',
    },
    hero: {
      eyebrow: 'PPF & EPF',
      title: 'Two provident funds, one family responsibility',
      lead: 'PPF and EPF sound similar, but they work differently. Families who understand both are far less likely to leave money unclaimed.',
    },
    intro: [
      'Many Indian households have both a Public Provident Fund (PPF) account and an Employee Provident Fund (EPF) balance. They are both long-term savings, but they come from different places: PPF is opened by you at a bank or post office, while EPF is built through employment.',
      'Families often know about one but not the other. A spouse might remember the EPF from a current job but have no idea about a PPF account opened ten years ago at a hometown post office.',
      'KeepMyAsset lets you record both clearly—so your nominees see the full picture, not just half of your provident fund story.',
    ],
    whyMatters: {
      title: 'PPF vs EPF — quick comparison',
      points: [
        'PPF: Personal account you open; 15-year scheme; held at bank or post office.',
        'EPF: Linked to jobs; monthly contributions; tracked through UAN on EPFO portal.',
        'Both can have significant balances after years of saving.',
        'Both need nominee updates and clear family records to avoid unclaimed funds.',
      ],
    },
    whatToStore: {
      title: 'Record both separately for clarity',
      items: [
        'PPF: bank/post office, account number, nominee, maturity date',
        'EPF: UAN, employers, passbook, nominee at EPFO',
        'Note which is which—label them clearly for your family',
        'Upload one recent statement for each account type',
      ],
    },
    commonProblems: {
      title: 'The confusion families face',
      items: [
        'Treating PPF and EPF as the same account and searching in the wrong place.',
        'Knowing about EPF from the last job but missing balances from earlier employers.',
        'Forgetting a PPF account after moving to a new city.',
      ],
    },
    tips: {
      title: 'One checklist for both',
      items: [
        'Create separate entries in KeepMyAsset for PPF and EPF.',
        'Review nominees on both at least once a year.',
        'After a job change, update EPF; after a tax-saving deposit, update PPF.',
        'Tell your nominee both types exist—even if balances seem small today.',
      ],
    },
    faqs: [
      {
        q: 'Can I merge PPF and EPF?',
        a: 'No. They are separate schemes with different rules. Your family needs to know about each one individually.',
      },
    ],
    related: ['ppf', 'epf', 'bank-fd'],
  },
  {
    slug: 'insurance',
    path: '/assets/insurance',
    label: 'Insurance',
    gridLabel: 'Insurance',
    pillLabel: 'Insurance & LIC',
    variant: 'warm',
    seo: {
      title: 'Insurance & LIC Policies',
      description:
        'Life insurance, LIC, health cover, and general insurance—record every policy so your family can file claims on time. Protect insurance from going unclaimed.',
      keywords: 'LIC nominee, life insurance family, unclaimed insurance India, health insurance records, policy nominee alert',
    },
    hero: {
      eyebrow: 'INSURANCE & LIC',
      title: 'Insurance only protects your family if they know it exists',
      lead: 'Life policies, LIC plans, health cover, and vehicle insurance often expire unclaimed—not because families do not care, but because they were never told.',
    },
    intro: [
      'Insurance is one of the most painful assets to lose. A life insurance policy or LIC plan may represent years of premiums and lakhs of rupees in cover—yet families frequently discover policies only after the claim window has narrowed or contact with the insurer was never made.',
      'Policies may be spread across LIC, private insurers, employer group cover, vehicle insurance, and home insurance. Premium receipts might arrive by SMS or email that stops after a phone number changes.',
      'Recording every policy in KeepMyAsset gives your nominees a starting map: which insurer, what type of cover, policy number reference, and where the documents are stored.',
    ],
    whyMatters: {
      title: 'Why insurance goes unclaimed more than any other asset',
      points: [
        'Policies are invisible until someone needs them—there is no monthly statement like a bank account.',
        'Employer-provided group insurance ends when employment ends; families may not know cover existed.',
        'Premium auto-debit continues quietly; families assume "nothing to claim" if they never saw a policy document.',
        'Nominee details on older policies may list parents or siblings who are no longer the right contact.',
      ],
    },
    whatToStore: {
      title: 'What to record for each insurance policy',
      items: [
        'Insurer name (LIC, HDFC Life, ICICI Lombard, etc.)',
        'Policy type—life, term, endowment, health, motor, home',
        'Policy number or last few digits',
        'Sum assured or cover amount (approximate is fine)',
        'Premium payment mode—annual, monthly, employer-paid',
        'Nominee name on the policy',
        'Agent name and contact, if you have one',
        'Policy document or premium receipt photo',
        'Renewal date for general insurance (health, motor)',
      ],
    },
    commonProblems: {
      title: 'Stories families regret not preparing for',
      items: [
        'A term plan worth ₹1 crore existed, but the family found the papers two years too late.',
        'LIC policy was in the father\'s name; premium SMS went to an old mobile number.',
        'Health insurance through an employer was never mentioned to the spouse.',
        'Vehicle insurance claim was possible, but no one knew which company held the policy.',
      ],
    },
    tips: {
      title: 'Keep your insurance visible to the people who matter',
      items: [
        'List every policy—even small ones and employer group covers.',
        'Update nominee details after major life events like marriage or children.',
        'Store one photo of each policy schedule in KeepMyAsset.',
        'Note whether premiums are auto-debited so your family knows payments may still be active.',
        'Include both life insurance and general insurance; families often search for only one type.',
      ],
    },
    faqs: [
      {
        q: 'Does KeepMyAsset file insurance claims?',
        a: 'No. We help your family identify policies and find documents. Claims are filed directly with the insurer using their official process.',
      },
      {
        q: 'Should I upload full policy documents?',
        a: 'Yes, if you are comfortable. Documents are encrypted and stored securely. They give your family the exact details insurers ask for during a claim.',
      },
    ],
    related: ['bank-fd', 'will', 'stocks-mf'],
  },
  {
    slug: 'stocks-mf',
    path: '/assets/stocks-mf',
    label: 'Stocks & MF',
    gridLabel: 'Stocks & MF',
    pillLabel: 'Stocks & mutual funds',
    variant: 'default',
    seo: {
      title: 'Stocks & Mutual Funds',
      description:
        'Demat accounts, mutual fund folios, SIPs, and stock holdings—record investments so your family can trace every rupee in the market.',
      keywords: 'mutual fund nominee, demat account family, stock investment records India, SIP family guide, unclaimed mutual funds',
    },
    hero: {
      eyebrow: 'STOCKS & MUTUAL FUNDS',
      title: 'Your investments in the market should not vanish in silence',
      lead: 'Demat accounts, mutual fund folios, and SIPs grow over years. Without a map, even financially aware families struggle to find them all.',
    },
    intro: [
      'Indian investors today hold stocks through demat accounts, mutual funds through multiple apps and registrars, SIPs that run automatically, and sometimes old physical share certificates or bond holdings from earlier decades.',
      'Each investment may use a different email, mobile number, or platform. After a death in the family, nominees must contact registrars, brokers, and fund houses—often without knowing which platforms were used or whether a folio still exists.',
      'KeepMyAsset helps you list every investment account, upload key statements, and guide your family without giving them your passwords or trading access.',
    ],
    whyMatters: {
      title: 'Why market investments are hard for families to trace',
      points: [
        'Multiple apps and brokers spread holdings across platforms families have never heard of.',
        'SIPs continue from bank accounts families do not associate with "investments."',
        'Old mutual fund folios may sit with registrars like CAMS or KFintech under outdated email addresses.',
        'Demat accounts with small balances are forgotten but can still hold valuable holdings.',
      ],
    },
    whatToStore: {
      title: 'What to record for stocks and mutual funds',
      items: [
        'Broker or platform name (Zerodha, Groww, HDFC Securities, etc.)',
        'Demat account number or client ID (partial is acceptable)',
        'Mutual fund folio numbers and fund names',
        'Registrar details (CAMS, KFintech) if known',
        'Nominee registered with the broker or fund house',
        'Latest holding statement or portfolio screenshot',
        'Linked bank account used for SIPs',
        'Any physical share certificates and where they are kept',
      ],
    },
    commonProblems: {
      title: 'What families discover too late',
      items: [
        'Parent invested through three apps; family only knew about one.',
        'SIP of ₹5,000/month ran for 15 years—family never connected it to a folio.',
        'Demat account had migrated brokers; old statements pointed to the wrong company.',
        'Nominee on the mutual fund was never updated after marriage.',
      ],
    },
    tips: {
      title: 'Investment records that actually help',
      items: [
        'Download a consolidated mutual fund statement from CAMS or KFintech once a year.',
        'List every broker and app—even experimental accounts with small balances.',
        'Update nominees on demat and folio accounts when your life situation changes.',
        'Note which email and phone number each platform uses—families need this for recovery.',
      ],
    },
    faqs: [
      {
        q: 'Will nominees get my trading password?',
        a: 'Never. KeepMyAsset does not share passwords or login access. Nominees receive guidance and documents to start official processes with brokers and registrars.',
      },
      {
        q: 'What about crypto or international stocks?',
        a: 'You can record any investment type. The same principle applies: name the platform, store reference documents, and tell your family where to begin.',
      },
    ],
    related: ['bank-fd', 'insurance', 'property'],
  },
  {
    slug: 'property',
    path: '/assets/property',
    label: 'Property',
    gridLabel: 'Property',
    pillLabel: 'Property & loans',
    variant: 'warm',
    seo: {
      title: 'Property & Real Estate Records',
      description:
        'Homes, land, rental property, and registration documents—help your family protect property assets with clear records in KeepMyAsset.',
      keywords: 'property documents family, sale deed nominee, real estate inheritance India, property records secure storage',
    },
    hero: {
      eyebrow: 'PROPERTY & REAL ESTATE',
      title: 'Property is often your largest asset—and the hardest to locate on paper',
      lead: 'Sale deeds, khata certificates, rental agreements, and plot papers scattered across folders can paralyze a family when they need clarity most.',
    },
    intro: [
      'For most Indian families, property represents the biggest financial asset: a home, a plot of land, an ancestral house, or a rental flat in another city. But property is also the most document-heavy asset—sale deeds, registration papers, tax receipts, society share certificates, and loan documents may live in different places.',
      'When the person who "knew where everything was" is no longer there, families face months of searching through lockers, email inboxes, and relatives\' homes—sometimes discovering property they never knew the parent owned.',
      'KeepMyAsset gives you a secure home for property records, key identifiers, and notes that tell your family exactly what exists and where the original papers are kept physically.',
    ],
    whyMatters: {
      title: 'Why property records cannot wait',
      points: [
        'Missing sale deeds delay transfer and create legal stress for years.',
        'Rental property in another city may generate income no one knows to collect.',
        'Ancestral property disputes often start because documents were never centralized.',
        'Home loan insurance and property tax links are lost when papers are scattered.',
      ],
    },
    whatToStore: {
      title: 'What to record for each property',
      items: [
        'Property type—home, plot, commercial, agricultural, rental',
        'Full address and city',
        'Registration or survey number from the sale deed',
        'Co-owners or joint holders, if any',
        'Outstanding home loan lender and approximate balance',
        'Society name and flat number for apartments',
        'Property tax payment reference or municipal ward',
        'Location of original sale deed (bank locker, home safe, etc.)',
        'Scanned copy of sale deed or index-II extract',
        'Rental tenant contact and agreement expiry, if applicable',
      ],
    },
    commonProblems: {
      title: 'Property problems families face every day',
      items: [
        'Sale deed was in a locker, but no one knew which bank held the locker.',
        'Parent owned a flat in another city; children learned about it from a tax notice years later.',
        'Home loan was almost paid off, but insurance linked to the loan was never transferred.',
        'Khata or mutation was pending—family did not know until they tried to sell.',
      ],
    },
    tips: {
      title: 'Make property easy for your family to understand',
      items: [
        'Scan the most important pages of each sale deed—not necessarily every stamp page.',
        'Note where physical originals are stored; digital copies guide, originals prove.',
        'List co-owners and whether the property is self-occupied or rented.',
        'Link home loan details so your family sees property and debt together.',
        'Update records when you buy, sell, or inherit property.',
      ],
    },
    faqs: [
      {
        q: 'Does KeepMyAsset transfer property ownership?',
        a: 'No. Property transfer follows legal and registration processes. We help your family find documents and understand what exists.',
      },
      {
        q: 'Is it safe to upload sale deeds?',
        a: 'Yes. Your documents are encrypted and stored securely. Access is limited to you—and shared with nominees only through a verified family process.',
      },
    ],
    related: ['loans', 'will', 'insurance'],
  },
  {
    slug: 'loans',
    path: '/assets/loans',
    label: 'Loans',
    gridLabel: 'Loans',
    variant: 'dark',
    seo: {
      title: 'Loans & Liabilities',
      description:
        'Home loans, personal loans, credit cards, and vehicle loans—record liabilities so your family knows what is owed and what is insured.',
      keywords: 'home loan family records, loan nominee India, credit card liability family, vehicle loan documents',
    },
    hero: {
      eyebrow: 'LOANS & LIABILITIES',
      title: 'Families need to know what is owed—not just what is owned',
      lead: 'Loans, EMIs, and credit lines do not disappear. Clear records protect your family from surprise calls and help them close accounts properly.',
    },
    intro: [
      'When we talk about "assets," we often forget liabilities. But for a grieving family, an unknown home loan, personal loan, or credit card balance can cause as much stress as a missing bank account—sometimes more.',
      'Loan documents may be with a builder, a bank branch, or lost in email. Insurance linked to a home loan might cover the outstanding amount, but only if someone knows the policy exists and contacts the insurer in time.',
      'Recording loans in KeepMyAsset is not about shame—it is about honesty and protection. Your family deserves to know what must be paid, what is insured, and who to call at each lender.',
    ],
    whyMatters: {
      title: 'Why loan records matter as much as assets',
      points: [
        'Unknown EMIs can default and damage family credit before anyone realizes.',
        'Home loan insurance may settle the outstanding balance—if the family files in time.',
        'Co-borrowed loans create legal obligations survivors must understand quickly.',
        'Credit cards and personal loans on auto-debit drain accounts after death if not closed.',
      ],
    },
    whatToStore: {
      title: 'What to record for each loan or liability',
      items: [
        'Lender name and loan type—home, personal, vehicle, education, gold',
        'Approximate outstanding balance and EMI amount',
        'Loan account number or last four digits',
        'Co-borrower or guarantor names',
        'Property linked to a home loan, if any',
        'Loan insurance or credit protection policy details',
        'Maturity or expected closure date',
        'Branch contact or relationship manager',
        'Sanction letter or latest statement photo',
      ],
    },
    commonProblems: {
      title: 'Loan surprises families encounter',
      items: [
        'Home loan EMI continued for months because no one knew which account was debited.',
        'Personal loan co-signed for a relative became the family\'s responsibility overnight.',
        'Vehicle loan insurance was never claimed after an accident during a difficult period.',
        'Multiple credit cards with small balances accumulated fees no one tracked.',
      ],
    },
    tips: {
      title: 'Honest records help everyone',
      items: [
        'List every ongoing loan, even if you plan to close it soon.',
        'Note linked insurance—home loan cover can save your family from paying the full balance.',
        'Tell your nominee which bank account each EMI uses.',
        'Include informal family loans if they affect your estate planning.',
        'Review loan records when you refinance or pay off a balance.',
      ],
    },
    faqs: [
      {
        q: 'Should I record loans if they will be paid off soon?',
        a: 'Yes. Plans change. A clear record today prevents confusion tomorrow if something unexpected happens.',
      },
      {
        q: 'Does KeepMyAsset negotiate with lenders?',
        a: 'No. We help your family understand liabilities. All settlement happens directly with banks and lenders.',
      },
    ],
    related: ['property', 'insurance', 'bank-fd'],
  },
  {
    slug: 'property-loans',
    path: '/assets/property-loans',
    label: 'Property & Loans',
    pillLabel: 'Property & loans',
    variant: 'warm',
    seo: {
      title: 'Property & Loans — Assets and Liabilities Together',
      description:
        'Homes, land, and home loans are deeply connected. Learn why recording property and loans together gives your family the full picture.',
      keywords: 'home loan property records, real estate liability India, property loan family guide',
    },
    hero: {
      eyebrow: 'PROPERTY & LOANS',
      title: 'Your home is an asset and a responsibility—record both sides',
      lead: 'Property and loans often go together. Families who see the full picture make better decisions during difficult times.',
    },
    intro: [
      'For most households, property and loans are two sides of the same coin. You buy a home with a home loan. You inherit land that still has a small mortgage. You rent out a flat while paying EMIs on another.',
      'Families searching for property documents often discover loans they did not expect—or find valuable property while struggling with loan paperwork they cannot locate.',
      'KeepMyAsset encourages you to record property and linked loans together, so your nominees understand net worth, not just individual papers.',
    ],
    whyMatters: {
      title: 'Why the connection matters',
      points: [
        'Home loan insurance may pay off the property loan—family must know both exist.',
        'Selling property requires clear loan closure documents.',
        'Co-owned property with shared EMIs creates complex responsibilities.',
        'Rental income from one property may pay EMIs on another—families need the full map.',
      ],
    },
    whatToStore: {
      title: 'Record property and loans as a pair',
      items: [
        'Property address and registration details',
        'Lender name and outstanding home loan balance',
        'EMI amount and debit account',
        'Loan-linked insurance policy',
        'Co-borrowers and co-owners',
        'Scanned sale deed and latest loan statement',
      ],
    },
    commonProblems: {
      title: 'When property and loans are recorded separately',
      items: [
        'Family found the sale deed but not the home loan account—EMIs defaulted.',
        'Loan was fully insured, but no one connected the policy to the property.',
        'Two properties existed; loans were tracked on only one.',
      ],
    },
    tips: {
      title: 'See the full picture',
      items: [
        'Create linked entries in KeepMyAsset for each property and its loan.',
        'Note net equity: property value minus outstanding loan.',
        'Update both when you prepay, refinance, or sell.',
      ],
    },
    faqs: [
      {
        q: 'Should I use separate pages for property and loans?',
        a: 'Yes. KeepMyAsset has dedicated pages for each. Use this overview to understand how they connect, then record details on both pages.',
      },
    ],
    related: ['property', 'loans', 'insurance'],
  },
  {
    slug: 'will',
    path: '/assets/will',
    label: 'Will',
    gridLabel: 'Will',
    pillLabel: 'Will & documents',
    variant: 'default',
    seo: {
      title: 'Will & Important Documents',
      description:
        'Wills, nominations, passports, locker keys, and legal papers—store critical documents so your family knows your wishes and where to find them.',
      keywords: 'will documents family India, legal papers nominee, passport locker records, inheritance documents storage',
    },
    hero: {
      eyebrow: 'WILL & DOCUMENTS',
      title: 'Your wishes and papers should speak clearly when you cannot',
      lead: 'A will, nomination forms, and key identity documents often decide how smoothly your family moves forward—or how long they struggle.',
    },
    intro: [
      'Beyond bank accounts and investments, every family needs access to certain documents: a will, nomination forms, passport copies, Aadhaar references, power of attorney papers, and sometimes religious or cultural documents that matter for rituals and closure.',
      'These papers are rarely needed on a normal Tuesday—but when they are needed, they are needed urgently. Families search through cupboards, call relatives, and hire lawyers to find a will that might not exist—or exists but no one knows where.',
      'KeepMyAsset is not a law firm and does not write your will. We give you a secure place to store copies, note where originals are kept, and tell your nominees what you want them to know.',
    ],
    whyMatters: {
      title: 'Why document records prevent family conflict',
      points: [
        'Unclear wishes lead to disputes between siblings and relatives.',
        'Missing nomination forms delay insurance and bank claims by months.',
        'Passport, visa, and identity documents are needed for many legal steps.',
        'Locker keys without locker location information are useless to your family.',
      ],
    },
    whatToStore: {
      title: 'Important documents to record',
      items: [
        'Will—location of original and scanned copy if available',
        'Nomination forms for banks, insurance, and investments',
        'Passport, PAN, and Aadhaar reference copies',
        'Marriage certificate and birth certificates of dependents',
        'Power of attorney documents, if any',
        'Bank locker location, number, and key storage place',
        'Vehicle RC and insurance papers',
        'Digital account inventory (email, cloud storage)—reference only, not passwords',
        'Lawyer or executor contact details',
        'Any letter of wishes or instructions for your family',
      ],
    },
    commonProblems: {
      title: 'Document gaps that hurt families',
      items: [
        'Will existed but was never registered; family could not find the original.',
        'Multiple versions of a will created confusion about which was final.',
        'Nomination forms were filled for one bank but not others.',
        'Locker keys were in a drawer; no one knew which bank branch to visit.',
      ],
    },
    tips: {
      title: 'Document habits that bring peace of mind',
      items: [
        'Tell your family whether you have a will—and where the original is stored.',
        'Upload a scan to KeepMyAsset even if the original is with a lawyer.',
        'Review nominations across all assets once a year.',
        'Name an executor or trusted contact in your KeepMyAsset nominee list.',
        'Keep a simple "if something happens to me" note with practical next steps.',
      ],
    },
    faqs: [
      {
        q: 'Does KeepMyAsset create a legal will?',
        a: 'No. Consult a qualified lawyer for will drafting and registration. We store documents and guide your family to what exists.',
      },
      {
        q: 'Is a scanned will legally valid?',
        a: 'Legal validity depends on Indian law and whether an original signed will exists. A scan helps your family locate the original quickly—it does not replace proper legal advice.',
      },
    ],
    related: ['insurance', 'bank-fd', 'property'],
  },
];

export const assetHubMeta = {
  path: ASSET_HUB_PATH,
  seo: {
    title: 'All Asset Types',
    description:
      'Explore every asset type KeepMyAsset helps you protect—bank accounts, PPF, EPF, insurance, stocks, property, loans, wills, and more.',
    keywords: 'family asset types India, nominee alert categories, financial records family guide',
  },
  hero: {
    eyebrow: 'ALL ASSETS',
    title: 'Every asset your family should know about',
    lead: 'One secure place for bank accounts, government savings, investments, insurance, property, loans, and the documents that tie them together.',
  },
};

export const pathToCategory = Object.fromEntries(
  assetCategories.map((category) => [category.path, category])
);

export const slugToCategory = Object.fromEntries(
  assetCategories.map((category) => [category.slug, category])
);

export const gridLabelToPath = {
  'Bank & FD': '/assets/bank-fd',
  PPF: '/assets/ppf',
  EPF: '/assets/epf',
  Insurance: '/assets/insurance',
  'Stocks & MF': '/assets/stocks-mf',
  Property: '/assets/property',
  Loans: '/assets/loans',
  Will: '/assets/will',
};

export const pillLabelToPath = {
  'Bank & FD': '/assets/bank-fd',
  'PPF & EPF': '/assets/ppf-epf',
  'Stocks & mutual funds': '/assets/stocks-mf',
  'Insurance & LIC': '/assets/insurance',
  'Property & loans': '/assets/property-loans',
  'Will & documents': '/assets/will',
};

export function getCategoryByPath(path) {
  if (path === ASSET_HUB_PATH) return null;
  return pathToCategory[path] || null;
}

export function getAllAssetPaths() {
  return [ASSET_HUB_PATH, ...assetCategories.map((c) => c.path)];
}

/** Primary grid categories shown on the hub (excludes combined pill-only pages). */
export const hubGridCategories = assetCategories.filter(
  (c) => !['ppf-epf', 'property-loans'].includes(c.slug)
);
